import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, TextField,
  InputAdornment, Tooltip, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Login as LoginIcon, Search as SearchIcon,
  CheckCircle as SuccessIcon, Cancel as BlockedIcon,
} from '@mui/icons-material';
import { getSignInLogs } from '@services/signInLogService';

const DAY_OPTIONS = [7, 14, 30, 60, 90];

export default function SignInLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLogs(await getSignInLogs({ days }));
    } catch (e) {
      setError('Failed to load sign-in logs.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const filtered = logs.filter((l) => {
    const q = search.toLowerCase();
    return !q || l.email?.includes(q) || l.ip?.includes(q) || l.uid?.includes(q);
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <LoginIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Sign-in Logs</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          (retained 90 days)
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search email, IP, or UID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flexGrow: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Time range</InputLabel>
          <Select value={days} label="Time range" onChange={(e) => setDays(e.target.value)}>
            {DAY_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>Last {d} days</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>UID</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Block Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No sign-in logs found.
                </TableCell>
              </TableRow>
            ) : filtered.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {log.timestamp?.toDate
                    ? log.timestamp.toDate().toLocaleString()
                    : '—'}
                </TableCell>
                <TableCell>{log.email || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{log.ip || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Tooltip title={log.uid || ''}>
                    <span>{log.uid ? `${log.uid.slice(0, 10)}…` : '—'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {log.success ? (
                    <Chip icon={<SuccessIcon />} label="Success" size="small" color="success" variant="outlined" />
                  ) : (
                    <Chip icon={<BlockedIcon />} label="Blocked" size="small" color="error" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>{log.blockedReason || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing {filtered.length} of {logs.length} entries
        </Typography>
      )}
    </Box>
  );
}
